import { useCallback, useMemo, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { serverModelToInputModelConverter } from "./converter";
import { useSiteControllerSettingBaseFormModelMethods } from "./methods";
import { excludeKeys } from "../../type";
import { siteControllerSettingBaseInputDiscriminatedUnionZodSchema } from "../../validation-schema";

import type { ExcludeKeys, SiteControllerSettingBaseFormModel } from "../../type";
import type { SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType } from "../../validation-schema";
import type { ServerModel } from "../use-initialized/type";

type Props = {
	serverModel: ServerModel;
};

type ViewModel = SiteControllerSettingBaseFormModel["ViewModel"];
type InputModel = SiteControllerSettingBaseFormModel["InputModel"];
type QueryData = SiteControllerSettingBaseFormModel["QueryData"];
export type SiteControllerSettingBaseFormModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export const useCreateSiteControllerSettingBaseFormModel = ({ serverModel }: Props) => {
	const inputModel = serverModelToInputModelConverter({ serverModel });

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		// SiteControllerSettingBaseFormModelの生成に用いたvalidation-schemaとは異なるvalidation-schemaを指定してる
		SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		// SiteControllerSettingBaseFormModelの生成に用いたvalidation-schemaとは異なるvalidation-schemaを指定してる
		zodSchema: siteControllerSettingBaseInputDiscriminatedUnionZodSchema,
	});

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: SiteControllerSettingBaseFormModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				// SiteControllerSettingBaseFormModelの生成に用いたvalidation-schemaとは異なるvalidation-schemaを指定してる
				SiteControllerSettingBaseInputDiscriminatedUnionZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				// SiteControllerSettingBaseFormModelの生成に用いたvalidation-schemaとは異なるvalidation-schemaを指定してる
				zodSchema: siteControllerSettingBaseInputDiscriminatedUnionZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	const queryData: QueryData = useMemo(() => {
		return {
			roomKVList: [...serverModel.roomList.rooms, ...serverModel.roomList.dummy].map((room) => {
				return {
					name: room.name,
					roomID: room.roomID,
				};
			}),
			airHostSettingsBaseInfo: {
				airHostPartnerID: serverModel.siteControllerSetting.airHostSettings?.airHostPartnerID ?? "",
				airHostAPIAccessToken: serverModel.siteControllerSetting.airHostSettings?.airHostAPIAccessToken ?? "",
				airHostPropertyID: serverModel.siteControllerSetting.airHostSettings?.airHostPropertyID ?? "",
				chillnnRoomAndAirHostPricePlanMappings:
					serverModel.siteControllerSetting.airHostSettings?.chillnnRoomAndAirHostPricePlanMappings ?? [],
			},
			isSiteControllerRegistered: serverModel.hotelApplicationStatus.isSitecontrollerRegistered,
		};
	}, [
		serverModel.roomList.rooms,
		serverModel.roomList.dummy,
		serverModel.siteControllerSetting.airHostSettings?.airHostPartnerID,
		serverModel.siteControllerSetting.airHostSettings?.airHostAPIAccessToken,
		serverModel.siteControllerSetting.airHostSettings?.airHostPropertyID,
		serverModel.siteControllerSetting.airHostSettings?.chillnnRoomAndAirHostPricePlanMappings,
		serverModel.hotelApplicationStatus.isSitecontrollerRegistered,
	]);

	return {
		viewModel, //
		onSetViewModel,
		...useSiteControllerSettingBaseFormModelMethods({ viewModel, onSetViewModel }),
		queryData,
		resetViewModel,
	};
};
